export function formatUtcYmd(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Calendar windows in UTC so guest funnel and growth chart stay aligned */
export function analyticsPresetCalendarRange(preset) {
  const now = new Date();
  const y = now.getUTCFullYear();
  const mo = now.getUTCMonth();
  const d = now.getUTCDate();
  const todayStart = new Date(Date.UTC(y, mo, d));
  const tomorrowStart = new Date(todayStart.getTime() + 86400000);
  const dayMs = 86400000;
  if (preset === 'today') {
    return {
      startIso: todayStart.toISOString(),
      endIso: tomorrowStart.toISOString(),
      applied: { start: formatUtcYmd(todayStart), end: formatUtcYmd(todayStart) },
    };
  }
  if (preset === '7d') {
    const start = new Date(todayStart.getTime() - 6 * dayMs);
    return {
      startIso: start.toISOString(),
      endIso: tomorrowStart.toISOString(),
      applied: { start: formatUtcYmd(start), end: formatUtcYmd(todayStart) },
    };
  }
  if (preset === '30d') {
    const start = new Date(todayStart.getTime() - 29 * dayMs);
    return {
      startIso: start.toISOString(),
      endIso: tomorrowStart.toISOString(),
      applied: { start: formatUtcYmd(start), end: formatUtcYmd(todayStart) },
    };
  }
  return null;
}

export function formatGrowthLabel(id) {
  if (!id) return '';
  if (typeof id === 'string') return id;
  // Backwards-compat: older backend returned {year, month, day}
  if (typeof id === 'object' && (id.year || id.month || id.day)) {
    const y = String(id.year || '').padStart(4, '0');
    const m = String(id.month || '').padStart(2, '0');
    const d = String(id.day || '').padStart(2, '0');
    return [y, m, d].filter(Boolean).join('-');
  }
  return String(id);
}

export function formatConv(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return '—';
  return `${rate}%`;
}

export function formatPeriodId(id, kind = 'week') {
  if (!id) return '';
  if (typeof id === 'string') return id;
  if (kind === 'month' && (id.year || id.month)) {
    const month = Number(id.month);
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[month - 1] || month} ${id.year}`;
  }
  if (id.year || id.week) {
    return `${id.year} W${String(id.week ?? '').padStart(2, '0')}`;
  }
  return String(id);
}

export function loginFrequencyLabel(bucketId) {
  const labels = {
    1: '1 login',
    2: '2–4 logins',
    5: '5–9 logins',
    10: '10–19 logins',
    20: '20–49 logins',
    50: '50–99 logins',
    '50+': '100+ logins',
  };
  return labels[bucketId] || labels[Number(bucketId)] || `${bucketId} logins`;
}

export function formatVerificationRate(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '0%';
  if (Math.abs(n % 1) < 1e-9) return `${Math.round(n)}%`;
  return `${n.toFixed(1)}%`;
}

export function weekOverWeekGrowth(daily = []) {
  const rows = Array.isArray(daily) ? daily : [];
  const last7 = rows.slice(-7).reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const prev7 = rows.slice(-14, -7).reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  if (prev7 <= 0) {
    return {
      last7,
      prev7,
      rate: last7 > 0 ? 100 : 0,
      isNew: last7 > 0,
      label: last7 > 0 ? `+${last7}` : '0%',
    };
  }
  const rate = ((last7 - prev7) / prev7) * 100;
  const rounded = Math.abs(rate) >= 10 ? Math.round(rate) : Math.round(rate * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return {
    last7,
    prev7,
    rate,
    isNew: false,
    label: `${sign}${rounded}%`,
  };
}

export function formatGbp(n, currency = 'GBP') {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(n || 0));
  } catch {
    return `${Number(n || 0).toFixed(2)} ${currency}`;
  }
}

export function chartPaletteForTheme(theme) {
  return theme === 'dark'
    ? {
        line: '#C6A75E',
        lineFill: 'rgba(230, 230, 232, 0.06)',
        grid: 'rgba(230, 230, 232, 0.12)',
        tick: 'rgba(230, 230, 232, 0.75)',
        pie: ['rgba(230,230,232,0.50)', 'rgba(230,230,232,0.35)', 'rgba(230,230,232,0.22)', 'rgba(230,230,232,0.16)', 'rgba(230,230,232,0.10)'],
      }
    : {
        line: '#9F8442',
        lineFill: 'rgba(42, 42, 46, 0.05)',
        grid: 'rgba(42, 42, 46, 0.12)',
        tick: 'rgba(42, 42, 46, 0.70)',
        pie: ['rgba(42,42,46,0.55)', 'rgba(42,42,46,0.40)', 'rgba(42,42,46,0.28)', 'rgba(42,42,46,0.20)', 'rgba(42,42,46,0.14)'],
      };
}

export function lineChartOptions(palette, extra = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      ...extra.plugins,
    },
    scales: {
      x: {
        ticks: { color: palette.tick, maxRotation: extra.maxRotation ?? 0, minRotation: 0 },
        grid: { color: palette.grid },
      },
      y: {
        beginAtZero: true,
        ticks: { color: palette.tick, precision: extra.precision },
        grid: { color: palette.grid },
      },
    },
  };
}
